import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Modal,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useMessage from 'src/app/hooks/useMessage';
import {
  useDeleteTemplateCategoryMutation,
  useDeleteTemplateSubCategoryMutation,
  useGetAllCategoriesQuery,
  useGetTemplateCategoriesQuery,
} from 'src/app/services/claim/claimApi';
import DocumentCategoryForm from './DocumentCategoryForm';

const DocumentCategory = () => {
  const { t } = useTranslation('translations');
  const { showMessage } = useMessage();

  const { data: categories = [] } = useGetTemplateCategoriesQuery();
  const { data: subCategories = [] } = useGetAllCategoriesQuery();

  const [deleteSubCategory] = useDeleteTemplateSubCategoryMutation();
  const [deleteCategory] = useDeleteTemplateCategoryMutation();

  const [openModal, setOpenModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const handleOpen = (item = null) => {
    setSelectedItem(item);
    setOpenModal(true);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      const isSubCategory = itemToDelete.templateCategoryId !== undefined;

      if (isSubCategory) {
        await deleteSubCategory(itemToDelete.id).unwrap();
      } else {
        await deleteCategory({ id: itemToDelete.id }).unwrap();
      }

      showMessage({ message: t('SuccessfullyDeleted'), variant: 'success' });
    } catch (error) {
      showMessage({ message: t('ErrorOccured'), variant: 'error' });
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  // Build subtree for a given parentId
  const getSubTree = (parentId) =>
    subCategories
      .filter((sc) => Number(sc.parentId) === Number(parentId))
      .map((sc) => ({
        ...sc,
        children: getSubTree(sc.id),
      }));

  // Main categories WITH nested children
  const treeData = useMemo(() => {
    const rootSubs = getSubTree(0);
    return categories.map((cat) => ({
      ...cat,
      children: rootSubs.filter((sc) => Number(sc.templateCategoryId) === Number(cat.id)),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, subCategories]);

  // + button logic:
  // - If clicking on MAIN CATEGORY => parentId must be 0
  // - If clicking on SUBCATEGORY => parentId is subcategory id
  const handleAddChild = (item, level) => {
    const isMainCategory = level === 0 && item.templateCategoryId === undefined;

    handleOpen({
      templateCategoryId: isMainCategory ? item.id : item.templateCategoryId, // keep main category id
      parentId: isMainCategory ? 0 : item.id, // IMPORTANT FIX
    });
  };

  const renderTree = (items, level = 0) =>
    items.map((item) => (
      <Box key={`${level}-${item.id}`} sx={{ pl: level * 4 }}>
        <ListItem
          sx={{
            '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 },
            transition: 'background-color 0.2s ease',
          }}
          secondaryAction={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title={t('Edit')} placement="top" arrow>
                <EditIcon
                  className="cursor-pointer text-blue-600"
                  onClick={() => handleOpen(item)}
                />
              </Tooltip>
              <Tooltip title={t('Delete')} placement="top" arrow>
                <DeleteIcon
                  className="cursor-pointer text-red"
                  onClick={() => handleDeleteClick(item)}
                />
              </Tooltip>
            </Box>
          }
        >
          <ListItemText
            primary={item.nameRu}
            secondary={level === 0 ? t('Category') : t('Subcategory')}
          />
        </ListItem>

        {item.children?.length ? renderTree(item.children, level + 1) : null}

        <Button size="small" sx={{ ml: 4, mb: 1 }} onClick={() => handleAddChild(item, level)}>
          <AddIcon /> {t('AddSubcategory')}
        </Button>
      </Box>
    ));

  return (
    <Box className="p-20">
      <Box className="flex justify-between mb-20">
        <Typography variant="h5">{t('DocumentCategories')}</Typography>
        <Button variant="outlined" color="secondary" onClick={() => handleOpen(null)}>
          <AddIcon /> {t('Create')}
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <List>{renderTree(treeData)}</List>
      </Paper>

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'background.paper',
            p: 3,
            borderRadius: 2,
            width: 600,
          }}
        >
          <DocumentCategoryForm
            selectedItem={selectedItem}
            onClose={() => setOpenModal(false)}
            categories={treeData} // IMPORTANT: pass treeData so form can show nested parent options
          />
        </Box>
      </Modal>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setItemToDelete(null);
        }}
      >
        <DialogTitle>{t('ConfirmationOfDeletion')}</DialogTitle>
        <DialogContent>
          <Typography>{t('AreYouSureToDeleteSubcategory')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setItemToDelete(null);
            }}
          >
            {t('Cancel')}
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentCategory;
